import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import {
  getRespondent,
  getGMA,
  getPersonality,
  getInterest,
  reuseConsent,
} from 'ducks/respondent'
import Link from 'redux-first-router-link'
import cx from 'classnames'
import {
  getFirstUnansweredPersonality,
  getFirstUnansweredInterest,
} from 'sagas/pagingSaga'
import {
  toTerms,
  toGmaQuiz,
  toGmaQuizWelcome,
  toPersonalityQuiz,
  toInterestQuiz,
} from 'ducks/router'
import Mail from 'assets/icons/mail.svg'
import ClockIcon from 'assets/icons/grey-clock.svg'
import { formatDeadline, type DeadlineFields } from 'utils/dates'
import styles from './Quiz.module.scss'
import { isFinished, subtitle, type Plan } from './plan'

import warningImg from 'assets/icons/warning.png'

import { Card } from '../Card/Card'
import { WaveContainer } from '../WaveContainer/WaveContainer'
import { Loader } from 'components/shared/Loader/Loader'
import { Link as DownloadLink } from 'components/shared/Link/Link'
import { DownloadIcon } from 'components/shared/Icon/model'

import DotsSvg from 'assets/icons/dots.svg'
import TargetSvg from 'assets/icons/target.svg'
import ArrowSvg from 'assets/icons/arrow.svg'

type CardOwnProps = {
  uuid: string
  isOptional?: boolean
  showReuseButton?: boolean
}

const getCardButtonLabel = (status: string | undefined) => {
  return {
    completed: t`Test results`,
    created: t`Go to test`,
    started: t`Continue test`,
  }[status ?? '']
}

const GMACard = ({ uuid, isOptional, showReuseButton }: CardOwnProps) => {
  const { status } = useSelector(getGMA, shallowEqual)
  const dispatch = useDispatch()

  return (
    <Card
      image={TargetSvg}
      name={t`Ability Test`}
      isOptional={isOptional}
      description={t`An inductive reasoning test measures your ability to identify patterns and draw logical conclusions.​`}
      buttonLabel={getCardButtonLabel(status)}
      completed={status === 'completed'}
      onClick={() =>
        dispatch(
          status === 'created'
            ? toGmaQuizWelcome({ uuid })
            : toGmaQuiz({ uuid }),
        )
      }
      showReuseButton={showReuseButton}
      handleReuse={() => dispatch(reuseConsent(true, ['gma']))}
    />
  )
}

export const MailHint = () => {
  return (
    <div className={styles.hint}>
      <img src={Mail} alt={t`Email`} />
      <span>
        {t`If you have any questions do not hesitate to contact us at`}{' '}
        <a className={styles.link} href="mailto:ebber@ebber.com" key="email">
          ebber@ebber.com
        </a>
      </span>
    </div>
  )
}

export const DeadlineHint = ({
  deadline,
  deadline_display,
  deadline_tz,
  past_deadline,
  language = 'en',
}: DeadlineFields & { language?: string }) => {
  const deadlineText = formatDeadline(
    { deadline, deadline_display, deadline_tz },
    language,
  )

  if (!deadlineText) return null

  return (
    <div className={styles.hint}>
      <img src={ClockIcon} alt={t`Deadline`} />
      <span>
        {past_deadline
          ? t`The deadline was ${deadlineText}.`
          : t`Deadline: ${deadlineText}`}
      </span>
    </div>
  )
}

const TermsHint = ({ uuid }: { uuid: string }) => {
  return (
    <div className={styles.hint}>
      <span>
        {t`By commencing the tests, you accept the`}{' '}
        <Link
          className={styles.link}
          to={toTerms({ uuid })}
        >{t`terms of use and privacy`}</Link>
      </span>
    </div>
  )
}

const PersonalityCard = ({ uuid, showReuseButton }: CardOwnProps) => {
  const { status, answers, questions } = useSelector(
    getPersonality,
    shallowEqual,
  )
  const dispatch = useDispatch()
  const nextQuestion = questions
    ? getFirstUnansweredPersonality({ answers, questions })(0)
    : undefined

  return (
    <Card
      image={DotsSvg}
      name={t`Personality Test`}
      description={t`A personality test reveals your strengths and work style to enhance self-awareness.`}
      buttonLabel={getCardButtonLabel(status)}
      completed={status === 'completed'}
      onClick={() => dispatch(toPersonalityQuiz({ uuid, page: nextQuestion }))}
      showReuseButton={showReuseButton}
      handleReuse={() => dispatch(reuseConsent(true, ['personality']))}
    />
  )
}

const InterestCard = ({ uuid }: CardOwnProps) => {
  const { status, answers, questions } = useSelector(getInterest, shallowEqual)
  const dispatch = useDispatch()
  const isCompleted = status === 'completed'
  const nextQuestion = questions
    ? getFirstUnansweredInterest({ answers, questions })(0)
    : undefined

  return (
    <Card
      image={ArrowSvg}
      name={t`Interest Test`}
      description={t`An interest test reveals which talent areas you are most interested in and will thrive in.`}
      buttonLabel={getCardButtonLabel(status)}
      completed={isCompleted}
      onClick={() =>
        dispatch(toInterestQuiz({ uuid, page: nextQuestion ?? 0 }))
      }
      hideButton={isCompleted}
    />
  )
}

type DownloadResultsProps = {
  pdf?: string
  language?: string
  finished: boolean
}

const DownloadResults = ({ pdf, language, finished }: DownloadResultsProps) => {
  if (!finished || !pdf) return null

  const link = pdf.replace(/LC/g, String(language))
  const filename = link.includes('EBBER-report')
    ? link.substring(link.indexOf('EBBER-report'))
    : 'EBBER-report.pdf'

  return (
    <div className={styles.downloadResults}>
      <DownloadLink
        icon={DownloadIcon}
        label={t`Download results`}
        size="medium"
        to={link}
        isExternal
        filename={filename}
        color="primary"
        variant="filled"
      />
    </div>
  )
}

type QuizProps = DeadlineFields & {
  name?: string
  id?: string
  loading?: boolean
  personality?: any
  gma?: any
  interest?: any
  plan?: Plan
  has_previous_results?: Record<string, boolean>
  reuse_selection?: Record<string, boolean>
  pdf?: string
  language?: string
}

const Quiz = () => {
  const {
    name,
    id: uuid,
    loading,
    personality,
    gma,
    interest,
    plan,
    has_previous_results: hasPreviousResults,
    reuse_selection: reuseSelection,
    pdf,
    language,
    deadline,
    deadline_display,
    deadline_tz,
    past_deadline,
  }: QuizProps = useSelector(getRespondent)
  const applicationFinished = plan
    ? isFinished(plan, { personality, gma, interest })
    : false

  return (
    <WaveContainer>
      <div className={styles.centerContent}>
        {loading ? (
          <Loader isInline isFullScreen />
        ) : uuid ? (
          <>
            <div className={styles.title}>
              {applicationFinished ? (
                <span>{t`Congratulations`}!</span>
              ) : (
                <>
                  <span>{`${t`Welcome`}, `}</span>
                  <span className={styles.name}>{name}</span>
                </>
              )}
            </div>
            <div
              className={cx(styles.subtitle, {
                [styles.finished]: applicationFinished,
              })}
            >
              {plan ? subtitle(plan, applicationFinished) : null}
            </div>
            <div className={styles.cards}>
              {plan?.modules.map(({ module, required }) => {
                switch (module) {
                  case 'interest':
                    return <InterestCard key={module} uuid={uuid} />
                  case 'personality':
                    return (
                      <PersonalityCard
                        key={module}
                        uuid={uuid}
                        showReuseButton={
                          personality?.status !== 'completed' &&
                          hasPreviousResults?.['personality'] &&
                          !reuseSelection?.['personality']
                        }
                      />
                    )
                  case 'gma':
                    return (
                      <GMACard
                        key={module}
                        uuid={uuid}
                        isOptional={!required}
                        showReuseButton={
                          gma?.status !== 'completed' &&
                          hasPreviousResults?.['gma'] &&
                          !reuseSelection?.['gma']
                        }
                      />
                    )
                  default:
                    return null
                }
              })}
            </div>
            <DownloadResults
              pdf={pdf}
              language={language}
              finished={applicationFinished}
            />
          </>
        ) : (
          <div className={styles.invalidLinkBox}>
            <span className={styles.invalidLinkIcon} aria-hidden>
              <img alt="warning" src={warningImg} />
            </span>
            <span className={styles.invalidLinkText}>
              {t`This test link appears to be incorrect. Please use the link from your invitation email or contact support at`}{' '}
              <a
                className={styles.invalidLinkEmail}
                href="mailto:ebber@ebber.com"
              >
                ebber@ebber.com
              </a>
              .
            </span>
          </div>
        )}
      </div>

      <div className={styles.hints}>
        {uuid && !applicationFinished ? (
          <DeadlineHint
            {...{
              deadline,
              deadline_display,
              deadline_tz,
              past_deadline,
              language,
            }}
          />
        ) : null}
        {uuid ? <TermsHint uuid={uuid} /> : null}
        <MailHint />
      </div>
    </WaveContainer>
  )
}

export default Quiz
